import { useState, useRef, useEffect } from "react";

type VerificationCodeInputProps = {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
};

const VerificationCodeInput = ({ length = 6, value = "", onChange }: VerificationCodeInputProps) => {
  const [digits, setDigits] = useState<string[]>(Array(length).fill(""));
  const inputsRef = useRef<HTMLInputElement[]>([]);

  // sync value prop
  useEffect(() => {
    if (value) {
      const chars = value.split("").slice(0, length);
      setDigits([...chars, ...Array(length - chars.length).fill("")]);
    }
  }, [value, length]);

  const handleChange = (val: string, idx: number) => {
    if (!/^\d*$/.test(val)) { // only numbers
      return;
    }
    const newDigits = [...digits];
    newDigits[idx] = val;
    setDigits(newDigits);
    onChange?.(newDigits.join(""));

    // focus next input
    if (val && idx < length - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputsRef.current[i] = el!; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={(e) => handleChange(e.target.value, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          className="w-10 h-12 text-center text-lg border rounded focus:ring focus:outline-none"
        />
      ))}
    </div>
  );
};

export default VerificationCodeInput;
